import { useEffect, useState } from "react";
import axios from "axios";
import { FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rotating layout templates so the grid feels editorial rather than uniform
const LAYOUTS = [
  { span: "md:col-span-8", ratio: "aspect-[16/10]" },
  { span: "md:col-span-4", ratio: "aspect-square" },
  { span: "md:col-span-4", ratio: "aspect-square" },
  { span: "md:col-span-4", ratio: "aspect-[4/5]" },
  { span: "md:col-span-4", ratio: "aspect-square" },
  { span: "md:col-span-6", ratio: "aspect-[4/3]" },
  { span: "md:col-span-6", ratio: "aspect-[4/3]" },
];

/**
 * Renders uploaded Cloudinary photos for a given category.
 * Silent (renders nothing) when the collection is empty — so pages stay clean
 * before the owner uploads anything.
 */
export default function UploadedPhotos({ category, title, kicker }) {
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/photos`, { params: { category } })
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos([]))
      .finally(() => setLoaded(true));
  }, [category]);

  if (!loaded || photos.length === 0) return null;

  return (
    <section
      data-testid={`uploaded-photos-${category}`}
      className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24"
    >
      <FadeIn>
        <div className="flex items-center gap-4 mb-10">
          <span className="chapter-num">{kicker || "Archive"}</span>
          <span className="hairline flex-1 max-w-[120px]" />
          <span className="font-mono-label text-[#B8A891]">{photos.length} photograph{photos.length === 1 ? "" : "s"}</span>
        </div>
        {title && (
          <h2 className="font-display text-5xl md:text-6xl text-[#F2E7D0] mb-10 max-w-3xl">
            {title}
          </h2>
        )}
      </FadeIn>

      <div className="grid grid-cols-4 md:grid-cols-12 gap-4">
        {photos.map((p, i) => {
          const layout = LAYOUTS[i % LAYOUTS.length];
          return (
            <FadeIn key={p.id} delay={(i % 4) * 0.06} className={`col-span-4 ${layout.span}`}>
              <div className="group relative overflow-hidden">
                <img
                  src={p.url}
                  alt={p.title || category}
                  className={`w-full ${layout.ratio} object-cover vintage-img group-hover:scale-[1.03] transition-transform duration-700`}
                />
                {p.title && (
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#1C1913]/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="font-display text-xl text-[#F2E7D0]">{p.title}</div>
                  </div>
                )}
              </div>
            </FadeIn>
          );
        })}
      </div>
    </section>
  );
}
